import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  QUIZ_CATEGORIES,
  QUIZ_SECTIONS,
  getQuizById,
  getQuizBySlug,
  getQuizCategoryBySlug,
  getQuizzesByCategory,
  isValidQuizCategorySlug,
} from "./quizData";
import QuizCard from "./QuizCard";
import QuizViewer from "./QuizViewer";
import "./Quiz.css";

const getCategoryCountLabel = (count) =>
  `${count} ${count === 1 ? "quiz" : "quizzes"}`;

const getVisibleCategorySlug = (categorySlug) => categorySlug || "all";

const getQuizSubsections = (categorySlug, categoryQuizzes) => {
  const registeredSections = QUIZ_SECTIONS[categorySlug] || [];
  const registeredSectionSlugs = new Set(
    registeredSections.map((section) => section.slug),
  );
  const quizzesBySectionSlug = categoryQuizzes.reduce((sectionMap, quiz) => {
    const sectionSlug = quiz.sectionSlug || "uncategorized";
    const sectionQuizzes = sectionMap.get(sectionSlug) || [];
    sectionMap.set(sectionSlug, [...sectionQuizzes, quiz]);
    return sectionMap;
  }, new Map());

  const orderedRegisteredSections = registeredSections
    .map((section) => ({
      section,
      quizzes: quizzesBySectionSlug.get(section.slug) || [],
    }))
    .filter(({ quizzes }) => quizzes.length > 0);

  const fallbackSections = categoryQuizzes.reduce((sections, quiz) => {
    const sectionSlug = quiz.sectionSlug || "uncategorized";

    if (registeredSectionSlugs.has(sectionSlug)) {
      return sections;
    }

    const existingSection = sections.get(sectionSlug);
    if (existingSection) {
      existingSection.quizzes.push(quiz);
    } else {
      sections.set(sectionSlug, {
        section: {
          id: sectionSlug,
          label: quiz.section || "Other",
          slug: sectionSlug,
        },
        quizzes: [quiz],
      });
    }

    return sections;
  }, new Map());

  return [...orderedRegisteredSections, ...fallbackSections.values()];
};

const QuizListing = ({ activeCategorySlug, onSelectCategory, onOpenQuiz }) => {
  const visibleCategorySlug = getVisibleCategorySlug(activeCategorySlug);
  const visibleCategories = useMemo(
    () =>
      visibleCategorySlug === "all"
        ? QUIZ_CATEGORIES.filter((category) => category.slug !== "all")
        : QUIZ_CATEGORIES.filter(
            (category) => category.slug === visibleCategorySlug,
          ),
    [visibleCategorySlug],
  );

  const quizzesByCategory = useMemo(
    () =>
      visibleCategories.map((category) => ({
        category,
        quizzes: getQuizzesByCategory(category.slug),
      })),
    [visibleCategories],
  );

  return (
    <div className="quiz-listing">
      <header className="quiz-hero">
        <span className="quiz-eyebrow">Interview practice</span>
        <h1>Quiz</h1>
        <p>
          Compact technical quizzes for revising core development topics before
          interviews and implementation work.
        </p>
      </header>

      <div className="quiz-category-strip" aria-label="Quiz categories">
        {QUIZ_CATEGORIES.map((category) => (
          <button
            type="button"
            key={category.slug}
            className={`filter-btn ${
              visibleCategorySlug === category.slug ? "active" : ""
            }`}
            onClick={() => onSelectCategory(category)}
            aria-pressed={visibleCategorySlug === category.slug}
          >
            {category.label}
          </button>
        ))}
      </div>

      <div className="blog-category-sections quiz-category-sections">
        {quizzesByCategory.map(({ category, quizzes: categoryQuizzes }) => (
          <section className="blog-category-section" key={category.slug}>
            <div className="blog-category-heading">
              <h2>{category.label}</h2>
              <span>{getCategoryCountLabel(categoryQuizzes.length)}</span>
            </div>
            {categoryQuizzes.length > 0 ? (
              getQuizSubsections(category.slug, categoryQuizzes).map(
                ({ section, quizzes: sectionQuizzes }) => (
                  <div className="quiz-subsection" key={section.slug}>
                    <h3 className="quiz-subsection-title">{section.label}</h3>
                    <div className="quiz-card-grid quiz-subsection-grid">
                      {sectionQuizzes.map((quiz) => (
                        <QuizCard
                          key={quiz.id}
                          quiz={quiz}
                          onOpen={() => onOpenQuiz(quiz)}
                        />
                      ))}
                    </div>
                  </div>
                ),
              )
            ) : (
              <p className="quiz-empty-message">Coming soon.</p>
            )}
          </section>
        ))}
      </div>
    </div>
  );
};

const Quiz = () => {
  const { categorySlug, quizSlug } = useParams();
  const navigate = useNavigate();

  const legacyQuiz = categorySlug
    ? getQuizById(categorySlug) || getQuizBySlug(categorySlug)
    : null;
  const selectedQuiz = legacyQuiz || (quizSlug ? getQuizBySlug(quizSlug) : null);
  const isListingRoute = !selectedQuiz && !quizSlug;
  const activeCategorySlug =
    isListingRoute && (!categorySlug || categorySlug === "all")
      ? "all"
      : isListingRoute && isValidQuizCategorySlug(categorySlug)
        ? categorySlug
        : null;

  useEffect(() => {
    if (
      categorySlug &&
      !selectedQuiz &&
      !quizSlug &&
      categorySlug !== "all" &&
      !isValidQuizCategorySlug(categorySlug)
    ) {
      navigate("/quiz", { replace: true });
    }
  }, [categorySlug, navigate, quizSlug, selectedQuiz]);

  const openQuiz = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectCategory = (category) => {
    navigate(`/quiz/${category.slug}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToListing = () => {
    navigate("/quiz");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if ((quizSlug || categorySlug) && !selectedQuiz && !activeCategorySlug) {
    return (
      <div className="blog-content quiz-content">
        <div className="post-container quiz-not-found">
          <h1>Quiz not found</h1>
          <p>The quiz you are looking for is not registered yet.</p>
          <button type="button" className="primary-btn" onClick={backToListing}>
            View all quizzes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-layout quiz-layout">
      <main className="blog-content quiz-content">
        {selectedQuiz ? (
          <QuizViewer quiz={selectedQuiz} onBack={backToListing} />
        ) : (
          <QuizListing
            activeCategorySlug={
              getQuizCategoryBySlug(activeCategorySlug)?.slug || "all"
            }
            onSelectCategory={selectCategory}
            onOpenQuiz={openQuiz}
          />
        )}
      </main>
    </div>
  );
};

export default Quiz;
